import React, { useEffect, useMemo, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { collection, doc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import Seo from "../components/Seo";
import { auth, db } from "../firebase";

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
  return null;
};

const formatDateTime = (value) => {
  const date = toDate(value);
  if (!date) return "Just now";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};
const formatRelativeTime = (value) => {
  const date = toDate(value);
  if (!date) return "Just now";
  const diffMs = Date.now() - date.getTime();
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diffMs < hour) {
    const mins = Math.max(1, Math.floor(diffMs / minute));
    return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  }
  if (diffMs < day) {
    const hours = Math.max(1, Math.floor(diffMs / hour));
    return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  }
  const days = Math.max(1, Math.floor(diffMs / day));
  return `${days} day${days === 1 ? "" : "s"} ago`;
};
const toSummary = (value) => {
  const clean = (value || "").replace(/\s+/g, " ").trim();
  if (!clean) return "No details provided yet.";
  if (clean.length <= 220) return clean;
  return `${clean.slice(0, 220).trim()}...`;
};

const NewsHub = () => {
  const [user, setUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [readNewsById, setReadNewsById] = useState({});
  const [selectedPostId, setSelectedPostId] = useState("");
  const [savingReadState, setSavingReadState] = useState(false);

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (nextUser) => {
      setUser(nextUser || null);
      if (!nextUser) {
        setReadNewsById({});
      }
    });
    return () => unsubAuth();
  }, []);

  useEffect(() => {
    const unsubPosts = onSnapshot(
      collection(db, "newsPosts"),
      (snapshot) => {
        const nextPosts = snapshot.docs
          .map((item) => ({ id: item.id, ...(item.data() || {}) }))
          .filter((item) => item.published !== false)
          .sort((a, b) => {
            const aTime = toDate(a.createdAt)?.getTime() || 0;
            const bTime = toDate(b.createdAt)?.getTime() || 0;
            return bTime - aTime;
          });
        setPosts(nextPosts);
      },
      () => {
        setPosts([]);
      }
    );
    return () => unsubPosts();
  }, []);

  useEffect(() => {
    if (!user?.uid) return undefined;
    const unsubUser = onSnapshot(
      doc(db, "users", user.uid),
      (snapshot) => {
        const data = snapshot.exists() ? snapshot.data() : {};
        const map =
          data?.readNewsById && typeof data.readNewsById === "object" ? data.readNewsById : {};
        setReadNewsById(map);
      },
      () => {
        setReadNewsById({});
      }
    );
    return () => unsubUser();
  }, [user]);

  const unreadCount = useMemo(
    () => posts.reduce((count, post) => (readNewsById[post.id] ? count : count + 1), 0),
    [posts, readNewsById]
  );

  const markPostRead = async (postId) => {
    if (!user?.uid || !postId || readNewsById[postId]) return;
    const nextReadMap = {
      ...readNewsById,
      [postId]: new Date().toISOString()
    };
    setSavingReadState(true);
    setReadNewsById(nextReadMap);
    try {
      await setDoc(
        doc(db, "users", user.uid),
        {
          readNewsById: nextReadMap,
          updatedAt: serverTimestamp()
        },
        { merge: true }
      );
    } catch {
      // Rollback local optimistic update only if save fails.
      setReadNewsById((prev) => {
        const rollback = { ...prev };
        delete rollback[postId];
        return rollback;
      });
    } finally {
      setSavingReadState(false);
    }
  };

  const markAllRead = async () => {
    if (!user?.uid || posts.length === 0) return;
    const nextReadMap = posts.reduce(
      (acc, post) => ({ ...acc, [post.id]: readNewsById[post.id] || new Date().toISOString() }),
      {}
    );
    setSavingReadState(true);
    setReadNewsById(nextReadMap);
    try {
      await setDoc(
        doc(db, "users", user.uid),
        {
          readNewsById: nextReadMap,
          updatedAt: serverTimestamp()
        },
        { merge: true }
      );
    } catch {
      // Keep UI stable on failure; listener will refresh state from cloud.
    } finally {
      setSavingReadState(false);
    }
  };

  return (
    <main className="up-page up-page-news container">
      <Seo
        title="News Hub"
        description="Platform updates and announcements for OctoGames players."
        path="/news"
      />
      <section className="news-hub">
        <div className="news-hub-top">
          <div>
            <h1 className="news-hub-title">News Hub</h1>
            <p className="news-hub-subtitle">
              Platform updates, announcements, and new features for players.
            </p>
          </div>
          <div className="news-hub-controls">
            {user ? (
              <>
                <span className="news-hub-unread-pill">
                  Unread: {unreadCount}
                </span>
                <button
                  type="button"
                  className="news-hub-mark-all"
                  onClick={markAllRead}
                  disabled={savingReadState || unreadCount === 0}
                >
                  Mark all as read
                </button>
              </>
            ) : (
              <span className="news-hub-signin-tip">
                Sign in to track unread news.
              </span>
            )}
          </div>
        </div>

        <div className="news-hub-list">
          {posts.map((post) => {
            const isRead = !user || Boolean(readNewsById[post.id]);
            const isOpen = selectedPostId === post.id;
            return (
              <article
                key={post.id}
                className={`news-post-card ${isRead ? "news-post-read" : "news-post-unread"} ${isOpen ? "news-post-open" : ""}`}
              >
                <button
                  type="button"
                  className="news-post-toggle"
                  onClick={() => {
                    setSelectedPostId((prev) => (prev === post.id ? "" : post.id));
                    if (!isRead) {
                      markPostRead(post.id);
                    }
                  }}
                >
                  <div className="news-post-thumb-wrap">
                    {post.image ? (
                      <img
                        src={post.image}
                        alt={post.title || "News image"}
                        className="news-post-thumb"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <div className="news-post-thumb news-post-thumb-fallback">📰</div>
                    )}
                  </div>
                  <div className="news-post-main">
                    <div className="news-post-header">
                      <h2 className="news-post-title">{post.title || "Platform update"}</h2>
                      {!isRead && <span className="news-post-unread-dot">Unread</span>}
                    </div>
                    <p className="news-post-summary">{toSummary(post.content)}</p>
                    <div className="news-post-meta">
                      <span>{formatRelativeTime(post.createdAt)}</span>
                    </div>
                  </div>
                </button>
                {isOpen && (
                  <div className="news-post-body">
                    <div className="news-post-body-date">{formatDateTime(post.createdAt)}</div>
                    {post.content || "No details provided."}
                  </div>
                )}
              </article>
            );
          })}
          {posts.length === 0 && (
            <p className="news-hub-empty">No news yet. Check back soon.</p>
          )}
        </div>
      </section>
    </main>
  );
};

export default NewsHub;

